import { useMemo, useState } from 'react';
import { Check, ChevronRight, RotateCcw } from 'lucide-react';
import type { PermissionModule, UserPermissionMatrix } from '../../lib/permissionsApi';
import type { RoleTemplate } from '../../lib/permissionsApi';
import {
  MODEL_ACCESS_OPTIONS,
  PERMISSION_MATRIX_CONFIG,
  type ModuleConfig,
} from './permissionMatrixConfig';
import { Select } from '../ui/Select';

export interface RbacMatrixEditorProps {
  /** The matrix being edited. */
  value: UserPermissionMatrix;
  onChange: (next: UserPermissionMatrix) => void;
  /** Available role templates for the pre-fill dropdown. */
  templates?: RoleTemplate[];
  /** Currently selected template id (controlled). */
  templateId?: string | null;
  /** Called when the template dropdown changes (also pre-fills the matrix). */
  onSelectTemplate?: (templateId: string | null, template: RoleTemplate | null) => void;
  /** Show the role-template dropdown + reset control. Default true. */
  showTemplatePicker?: boolean;
  /** Disable all inputs (read-only system template view). */
  disabled?: boolean;
}

// Read/write a single flag on a module without losing type-safety at runtime.
function getFlag(matrix: UserPermissionMatrix, module: PermissionModule, key: string): unknown {
  const mod = matrix[module] as unknown as Record<string, unknown>;
  return mod?.[key];
}

/** How much of a module is granted: its gate, and on/total over every flag. */
function moduleSummary(config: ModuleConfig, matrix: UserPermissionMatrix) {
  const keys = [...config.crud.map((c) => c.key), ...config.sub.map((s) => s.key)];
  const on = keys.filter((k) => getFlag(matrix, config.module, k) === true).length;
  const gateKey = config.crud[0]?.key;
  const gateOn = gateKey ? getFlag(matrix, config.module, gateKey) === true : true;
  return { on, total: keys.length, gateOn };
}

function hasDetails(config: ModuleConfig): boolean {
  return config.sub.length > 0 || Boolean(config.scope) || config.module === 'aiAssistant';
}

/**
 * Shared RBAC matrix editor (CLE-216, parent section 3.5). Renders every module
 * as a row group with View/Create/Edit/Delete checkboxes, an optional scope
 * dropdown, and nested sub-permission toggles. A parent toggle cascades to its
 * children (turning a parent off forces children off; toggling a parent on does
 * not auto-enable children, matching deny-by-default). Used both inline in the
 * Create Team Member modal and on the standalone Roles & Permissions page.
 */
export function RbacMatrixEditor({
  value,
  onChange,
  templates = [],
  templateId = null,
  onSelectTemplate,
  showTemplatePicker = true,
  disabled = false,
}: RbacMatrixEditorProps) {
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) ?? null,
    [templates, templateId],
  );

  function setFlag(module: PermissionModule, key: string, next: boolean) {
    const config = PERMISSION_MATRIX_CONFIG.find((c) => c.module === module);
    const mod = { ...(value[module] as unknown as Record<string, unknown>) };
    mod[key] = next;

    // Parent cascade: when a parent flag turns off, force its children off.
    if (!next && config) {
      for (const sub of config.sub) {
        if (sub.parent === key) mod[sub.key] = false;
      }
      // CRUD parents (canView) may gate subs too.
      for (const sub of config.sub) {
        if (sub.parent === key) mod[sub.key] = false;
      }
    }

    onChange({ ...value, [module]: mod } as UserPermissionMatrix);
  }

  function setScope(module: PermissionModule, key: string, next: string) {
    const mod = { ...(value[module] as unknown as Record<string, unknown>) };
    mod[key] = next;
    onChange({ ...value, [module]: mod } as UserPermissionMatrix);
  }

  function handleTemplateChange(id: string) {
    const tmpl = templates.find((t) => t.id === id) ?? null;
    onSelectTemplate?.(id || null, tmpl);
    if (tmpl) {
      // Pre-fill the whole matrix from the template (overrides are then made on
      // top of this). Deep clone so later edits don't mutate the template.
      onChange(JSON.parse(JSON.stringify(tmpl.permissions)) as UserPermissionMatrix);
    }
  }

  function resetToTemplate() {
    if (!selectedTemplate) return;
    onChange(JSON.parse(JSON.stringify(selectedTemplate.permissions)) as UserPermissionMatrix);
  }

  // Modules start folded: the header line (state + main actions) is the
  // overview, the details open on demand.
  const [open, setOpen] = useState<Set<PermissionModule>>(new Set());
  function toggleOpen(module: PermissionModule) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  }
  const activeCount = PERMISSION_MATRIX_CONFIG.filter((c) => moduleSummary(c, value).gateOn).length;

  return (
    <div className="flex flex-col gap-3">
      {showTemplatePicker && (
        <div className="flex items-end gap-2">
          <Select
            label="Șablon de rol"
            value={templateId ?? ''}
            onChange={handleTemplateChange}
            disabled={disabled}
            options={[
              { value: '', label: 'Personalizat (fără șablon)' },
              ...templates.map((t) => ({
                value: t.id,
                label: t.isSystem ? t.name : `${t.name} (personalizat)`,
              })),
            ]}
            className="flex-1"
          />
          {selectedTemplate && !disabled && (
            <button
              type="button"
              onClick={resetToTemplate}
              className="ui-btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap"
              title="Renunțați la suprascrieri și resetați toate căsuțele la valorile implicite ale șablonului"
            >
              <RotateCcw size={14} />
              Resetează la șablon
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <span>
          {activeCount} din {PERMISSION_MATRIX_CONFIG.length} module active
        </span>
        <span className="flex items-center gap-3">
          <button
            type="button"
            onClick={() =>
              setOpen(new Set(PERMISSION_MATRIX_CONFIG.filter(hasDetails).map((c) => c.module)))
            }
            className="text-brand-600 hover:underline"
          >
            Extinde tot
          </button>
          <button
            type="button"
            onClick={() => setOpen(new Set())}
            className="text-brand-600 hover:underline"
          >
            Restrânge tot
          </button>
        </span>
      </div>

      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {PERMISSION_MATRIX_CONFIG.map((config) => (
          <ModuleRow
            key={config.module}
            config={config}
            matrix={value}
            disabled={disabled}
            open={open.has(config.module)}
            onToggleOpen={() => toggleOpen(config.module)}
            onToggle={setFlag}
            onScope={setScope}
          />
        ))}
      </div>
    </div>
  );
}

function ModuleRow({
  config,
  matrix,
  disabled,
  open,
  onToggleOpen,
  onToggle,
  onScope,
}: {
  config: ModuleConfig;
  matrix: UserPermissionMatrix;
  disabled: boolean;
  open: boolean;
  onToggleOpen: () => void;
  onToggle: (module: PermissionModule, key: string, next: boolean) => void;
  onScope: (module: PermissionModule, key: string, next: string) => void;
}) {
  const { module } = config;
  const { on, total, gateOn } = moduleSummary(config, matrix);
  const expandable = hasDetails(config);

  return (
    <div className={gateOn ? '' : 'bg-gray-50/60'}>
      <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={expandable ? onToggleOpen : undefined}
          aria-expanded={expandable ? open : undefined}
          className={`flex min-w-0 items-center gap-2 text-left ${expandable ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <ChevronRight
            size={15}
            className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''} ${
              expandable ? '' : 'invisible'
            }`}
          />
          <span className={`text-sm font-semibold ${gateOn ? 'text-gray-900' : 'text-gray-500'}`}>
            {config.label}
          </span>
          <StatusBadge on={on} total={total} gateOn={gateOn} />
        </button>
        <div className="flex flex-wrap items-center gap-1.5 pl-6 sm:pl-0">
          {config.crud.map((c, i) => (
            <ActionPill
              key={c.key}
              label={c.label}
              checked={getFlag(matrix, module, c.key) === true}
              // Create/Edit/Delete mean nothing without the first action.
              disabled={disabled || (i > 0 && !gateOn)}
              onChange={(next) => onToggle(module, c.key, next)}
            />
          ))}
        </div>
      </div>

      {expandable && open && (
        <div className="border-t border-border/60 bg-gray-50/40 px-4 py-3 pl-10">
          {!gateOn && (
            <p className="mb-2 text-xs text-gray-500">
              Bifează „{config.crud[0]?.label}” ca să poți acorda detaliile.
            </p>
          )}
          {(config.scope || module === 'aiAssistant') && (
            <div className="mb-3 flex flex-wrap gap-4">
              {config.scope && (
                <Select
                  label={config.scope.label}
                  size="sm"
                  value={String(getFlag(matrix, module, config.scope.key) ?? '')}
                  onChange={(v) => onScope(module, config.scope!.key, v)}
                  disabled={disabled || !gateOn}
                  options={config.scope.options}
                  className="w-52"
                />
              )}
              {module === 'aiAssistant' && (
                <Select
                  label="Model"
                  size="sm"
                  value={String(getFlag(matrix, module, 'modelAccess') ?? 'haiku')}
                  onChange={(v) => onScope(module, 'modelAccess', v)}
                  disabled={disabled || !gateOn}
                  options={MODEL_ACCESS_OPTIONS}
                  className="w-52"
                />
              )}
            </div>
          )}
          {config.sub.length > 0 && (
            <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {config.sub.map((sub) => {
                const parentOn = sub.parent ? getFlag(matrix, module, sub.parent) === true : true;
                const parentLabel =
                  config.crud.find((c) => c.key === sub.parent)?.label ??
                  config.sub.find((x) => x.key === sub.parent)?.label;
                return (
                  <Checkbox
                    key={sub.key}
                    label={sub.label}
                    checked={getFlag(matrix, module, sub.key) === true}
                    disabled={disabled || !parentOn}
                    hint={!parentOn && parentLabel ? `Necesită „${parentLabel}”` : undefined}
                    onChange={(next) => onToggle(module, sub.key, next)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** "Fără acces", "Complet" or "6/12": the module at a glance, while folded. */
function StatusBadge({ on, total, gateOn }: { on: number; total: number; gateOn: boolean }) {
  if (!gateOn) {
    return (
      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
        Fără acces
      </span>
    );
  }
  if (on === total) {
    return (
      <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
        Complet
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full bg-brand-500/10 px-2 py-0.5 text-[11px] font-medium text-brand-600">
      {on}/{total}
    </span>
  );
}

/** A main action as a toggle chip: tinted with a check when granted. */
function ActionPill({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        checked
          ? 'border-brand-500/40 bg-brand-500/10 text-brand-600'
          : 'border-border text-gray-500 hover:border-gray-300 hover:text-gray-700'
      } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
    >
      {checked && <Check size={12} strokeWidth={3} />}
      {label}
    </button>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Why it is disabled, shown on hover. */
  hint?: string;
}) {
  return (
    <label
      title={hint}
      className={`flex items-center gap-2 text-[13px] leading-5 text-gray-700 ${
        disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      <span>{label}</span>
    </label>
  );
}
